// Staffing model — « how many people, where, when » (C3 Workforce/RH).
//
// docs/PREDICTIVE-STAFFING-BRIEF.md §4 is canonical; this file is its executable
// form. Pure functions only: no I/O, no DB, no clock reads. Phase 1 of the
// brief's rollout deliberately ships BEFORE any schema, so the model can be
// validated against the operator's own figures before a migration exists.
//
// The one thing to understand before editing: a single `demand / demandPerHead`
// ratio cannot express any of the three real shapes. Ménage is a work-content
// calculation, Dîner is a tiered profile whose *decomposition* changes with
// volume, and Bar is a binary post that can be held CONCURRENTLY with another.
// Each archetype below exists because a ratio got it wrong.

import { evaluateFormula, timeToMinutes, type BoardDay, type DemandFormula } from './boardCoverage.js';
import type { ProfileOutputs } from './metricRegistry.js';

// ── Resolved output ─────────────────────────────────────────────────────────

/**
 * A concrete staffing requirement for one day — the ONLY thing the allocator
 * consumes. Archetypes are a configuration concern; by the time a post reaches
 * the allocator it is just « this many people, this window ».
 */
export interface ResolvedPost {
  /** Null for a tiered post that has no standalone activity row (e.g. the merged « Plonge + nettoyage »). */
  activityId: number | null;
  label: string;
  /** 'HH:MM' */
  windowStart: string;
  windowEnd: string;
  heads: number;
}

/** 'HH:MM' → minutes; minutes → 'HH:MM'. Kept local so the module is self-contained. */
export function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ── Archetype A — binary post, shareable ────────────────────────────────────

export interface BinarySpec {
  kind: 'binary';
  activityId: number;
  label: string;
  windowStart: string;
  windowEnd: string;
  /** Weighted sum over day metrics (reuses the existing demand formula machinery). */
  formula: DemandFormula;
  /** Below this the post is simply not staffed — the Bar is closed. */
  threshold: number;
}

export function resolveBinary(spec: BinarySpec, day: BoardDay | null): ResolvedPost[] {
  const demand = evaluateFormula(day, spec.formula);
  if (demand < spec.threshold) return [];
  return [
    {
      activityId: spec.activityId,
      label: spec.label,
      windowStart: spec.windowStart,
      windowEnd: spec.windowEnd,
      heads: 1,
    },
  ];
}

// ── Archetype B — work content (Ménage) ─────────────────────────────────────

/** Person-minutes per room event (brief §4.2, operator figures 2026-07-29). */
export interface RoomEventMinutes {
  aBlanc: number;
  recouche: number;
  recoucheAFond: number;
  arrivee: number;
}

export const DEFAULT_ROOM_MINUTES: RoomEventMinutes = {
  aBlanc: 30,
  recouche: 10,
  recoucheAFond: 30,
  arrivee: 5,
};

/**
 * The day's room work. `recouches` EXCLUDES `recouchesAFond` (they are counted
 * once, at the heavier rate) and `aBlancs` INCLUDES `turnovers` (a turnover is
 * an à blanc that also happens to be re-let today).
 */
export interface RoomWorkload {
  /** Departures — every room stripped and cleaned in full. */
  aBlancs: number;
  /**
   * The subset of `aBlancs` a guest arrives into the SAME day — the only
   * cleaning with a check-in deadline. Rooms are assigned at booking, so this
   * is an exact join in production; `boundTurnovers` is the fallback.
   */
  turnovers: number;
  /** Stayovers at the light rate. */
  recouches: number;
  /** Stayovers on their 4th/8th/12th night — redone almost à blanc. */
  recouchesAFond: number;
  arrivees: number;
}

/**
 * Upper bound on turnovers when room assignment is unknown (a booking that
 * landed overnight): at most `min(àBlancs, arrivées)` departures can be re-let.
 * Errs in the SAFE direction — it may start the shift earlier than strictly
 * needed, never later.
 */
export function boundTurnovers(aBlancs: number, arrivees: number): number {
  return Math.min(aBlancs, arrivees);
}

/**
 * Is tonight a deep-clean night for a stay on its `nightIndex`-th night?
 * Every 4th night (4, 8, 12) — NOT every night past the fourth, which would
 * turn a 12-night stay into 8 deep cleans instead of 3.
 */
export function isDeepCleanNight(nightIndex: number, cadence = 4): boolean {
  return nightIndex > 0 && nightIndex % cadence === 0;
}

/** Total person-minutes of room work for the day. */
export function workMinutes(load: RoomWorkload, minutes: RoomEventMinutes = DEFAULT_ROOM_MINUTES): number {
  return (
    minutes.aBlanc * load.aBlancs +
    minutes.recouche * load.recouches +
    minutes.recoucheAFond * load.recouchesAFond +
    minutes.arrivee * load.arrivees
  );
}

/**
 * Person-minutes that must be finished before check-in: the turnovers plus
 * every arrival's prep. NOT `aBlanc * aBlancs` — a departure room nobody
 * arrives into can be cleaned after check-in, and treating every departure as
 * urgent pulls the shift start earlier than the work requires on exactly the
 * heavy-departure / light-arrival days a shoulder season is made of.
 */
export function criticalMinutes(load: RoomWorkload, minutes: RoomEventMinutes = DEFAULT_ROOM_MINUTES): number {
  return minutes.aBlanc * load.turnovers + minutes.arrivee * load.arrivees;
}

/**
 * The comfort floor (operator: « at least 2, one only for a handful of rooms »),
 * measured in À-BLANC EQUIVALENTS — the day's work divided by one à blanc — so
 * it lives in the same currency as capacity and needs no room counter.
 *
 * It is deliberately NOT a productivity coefficient. Pairing is an elapsed-time
 * device, not a faster one (person-minutes are conserved: 1×30 = 2×15), so
 * folding « two is easier » into the arithmetic would both corrupt the correct
 * conserved-minutes maths and silently vanish on light days — precisely when
 * the operator still wants two people.
 */
export function aBlancEquivalents(load: RoomWorkload, minutes: RoomEventMinutes = DEFAULT_ROOM_MINUTES): number {
  return workMinutes(load, minutes) / minutes.aBlanc;
}

export function comfortFloor(load: RoomWorkload, threshold = 10, minutes: RoomEventMinutes = DEFAULT_ROOM_MINUTES): number {
  // NO work means NO ménage. The operator's rule — « at least 2, one only for a
  // handful of rooms » — presupposes there are rooms; « a handful » is not zero.
  // Without this the floor demands a cleaner on a day with nothing to clean (a
  // closed period, or an empty shoulder-season Tuesday), which the board would
  // then report as an uncovered activity and the 18:00 job would broadcast as a
  // poste à pourvoir. Found by sweeping the model over realistic days rather
  // than by a test asserting what it already believed.
  if (workMinutes(load, minutes) === 0) return 0;
  return aBlancEquivalents(load, minutes) > threshold ? 2 : 1;
}

export interface WorkContentSpec {
  kind: 'workContent';
  activityId: number;
  /**
   * The `board_staffing_profile` row this spec came from. Carried so the profile
   * can PUBLISH its outputs as named metrics (ADR-0015) — the registry keys them
   * by profile id, not by activity, because a profile may staff several
   * activities while publishing one load.
   */
  profileId: number;
  label: string;
  minutes: RoomEventMinutes;
  /** À-blanc equivalents above which two people are staffed regardless of capacity. */
  comfortFloorThreshold: number;
  /** Never start before this, however heavy the day. */
  earliestStart: string;
  /** Never start after this, however light the day. */
  defaultStart: string;
  /** Hard end of the shift — work that cannot fit before it forces another pair of hands. */
  latestEnd: string;
  /**
   * Upper bound for the heads/time trade-off (ADR-0015 §4): how many people the
   * frontier may enumerate, and how far `targetFinish` may escalate.
   *
   * Deliberately NOT a cap on `capacityHeads`. Capping the volume-driven figure
   * would silently under-staff a day that genuinely needs more hands, turning a
   * visible « il manque quelqu'un » into an invisible one — the exact failure
   * mode the comfort floor's zero-work guard was added to prevent.
   */
  maxHeads?: number;
  /**
   * « Finished by » target. Unset (the default) keeps the historic behaviour —
   * minimise heads, accept the later finish. Set, `resolveMenage` takes the
   * SMALLEST head count that finishes by this time, up to `maxHeads`, and
   * reports failure rather than pretending to have met it.
   */
  targetFinish?: string;
}

/** Frontier bound when a profile does not configure one. */
export const DEFAULT_MAX_HEADS = 6;

export const DEFAULT_MENAGE_SPEC: Omit<WorkContentSpec, 'activityId' | 'profileId' | 'label'> = {
  kind: 'workContent',
  minutes: DEFAULT_ROOM_MINUTES,
  comfortFloorThreshold: 10,
  earliestStart: '08:00',
  defaultStart: '09:00',
  latestEnd: '17:00',
  maxHeads: DEFAULT_MAX_HEADS,
};

export interface MenageResolution extends ResolvedPost {
  /** Heads the work volume alone requires. */
  capacityHeads: number;
  /** Heads the « two is easier » rule requires. */
  comfortFloor: number;
  /** Total person-minutes; surfaced so the desk can see WHY. */
  workMinutes: number;
  /** Deadline-bound share of it. */
  criticalMinutes: number;
  /**
   * Whether a configured `targetFinish` was reached. Null when none is set —
   * the three states (« met », « missed », « none asked for ») are distinct and
   * a boolean would conflate the last two into a permanent false.
   */
  meetsTargetFinish: boolean | null;
}

// ── The heads / time trade-off ──────────────────────────────────────────────

/**
 * One point on the frontier: what this many people implies for the day.
 *
 * The trade-off is an identity, not a model — person-minutes are conserved
 * under pairing (brief §4.2, « un à blanc c'est 1×30 ou 2×15 »), so
 * `work = heads × elapsed` holds exactly and no efficiency coefficient belongs
 * anywhere near it.
 */
export interface TradeoffOption {
  heads: number;
  windowStart: string;
  /**
   * TRUE finish, deliberately NOT capped at `latestEnd` — « 3 personnes → 18:40 »
   * is the fact that makes the option obviously bad. `resolveMenage` caps its
   * own output; the frontier must not, or every infeasible option would render
   * as finishing exactly at closing time.
   */
  windowEnd: string;
  /** Person-minutes ÷ heads. */
  elapsedMinutes: number;
  /** Finishes at or before `latestEnd`. */
  fitsInDay: boolean;
  /** The check-in-critical share is done by `readyBy`. */
  meetsDeadline: boolean;
}

/**
 * Every feasible head count for a day, with what each implies — the choice the
 * operator actually makes each morning (« 2 pers → 13:30 · 3 pers → 12:00 »).
 *
 * Previously invisible: `resolveMenage` returned one answer with no indication
 * that another existed, so the desk could not see what a third pair of hands
 * would buy, nor what accepting a later finish would save.
 */
export function tradeoffFrontier(
  spec: WorkContentSpec,
  load: RoomWorkload,
  readyBy: string,
): TradeoffOption[] {
  const minutes = spec.minutes;
  const total = workMinutes(load, minutes);
  if (total === 0) return [];

  const critical = criticalMinutes(load, minutes);
  const earliest = timeToMinutes(spec.earliestStart);
  const latest = timeToMinutes(spec.defaultStart);
  const end = timeToMinutes(spec.latestEnd);
  const deadline = timeToMinutes(readyBy);
  const maxHeads = Math.max(1, Math.floor(spec.maxHeads ?? DEFAULT_MAX_HEADS));

  const out: TradeoffOption[] = [];
  for (let heads = 1; heads <= maxHeads; heads += 1) {
    const start = Math.max(earliest, Math.min(latest, deadline - critical / heads));
    const elapsed = Math.ceil(total / heads);
    const finish = start + elapsed;
    out.push({
      heads,
      windowStart: minutesToTime(start),
      windowEnd: minutesToTime(finish),
      elapsedMinutes: elapsed,
      fitsInDay: finish <= end,
      meetsDeadline: start + Math.ceil(critical / heads) <= deadline,
    });
  }
  return out;
}

/**
 * Ménage: heads AND window together, because they are mutually dependent — heads
 * divides into the window, and the window sizes the capacity denominator.
 *
 * Resolved in ONE refinement pass rather than a fixed-point loop: size the start
 * from the deadline-critical work at the comfort floor, take the capacity that
 * window implies, then re-derive the window at the final head count. Two passes,
 * deterministic, no convergence to reason about.
 */
export function resolveMenage(
  spec: WorkContentSpec,
  load: RoomWorkload,
  readyBy: string,
): MenageResolution {
  const minutes = spec.minutes;
  const total = workMinutes(load, minutes);
  const critical = criticalMinutes(load, minutes);
  const floor = comfortFloor(load, spec.comfortFloorThreshold, minutes);

  const earliest = timeToMinutes(spec.earliestStart);
  const latest = timeToMinutes(spec.defaultStart);
  const end = timeToMinutes(spec.latestEnd);
  const deadline = timeToMinutes(readyBy);

  // A day with nothing to do: no people, no window. Returning early keeps every
  // division below safely away from zero heads.
  if (total === 0) {
    return {
      activityId: spec.activityId,
      label: spec.label,
      windowStart: minutesToTime(latest),
      windowEnd: minutesToTime(latest),
      heads: 0,
      capacityHeads: 0,
      comfortFloor: 0,
      workMinutes: 0,
      criticalMinutes: 0,
      meetsTargetFinish: null,
    };
  }

  const startFor = (heads: number): number => {
    const needed = heads > 0 ? critical / heads : critical;
    return Math.max(earliest, Math.min(latest, deadline - needed));
  };
  const finishFor = (heads: number): number => startFor(heads) + Math.ceil(total / heads);

  // Pass 1 — the window the comfort floor would work in.
  const provisionalStart = startFor(floor);
  const usable = Math.max(1, end - provisionalStart);
  const capacityHeads = Math.ceil(total / usable);

  // Pass 2 — final heads, final window.
  let heads = Math.max(floor, capacityHeads);

  // Pass 3 — the trade-off, and ONLY when a target is configured (ADR-0015 §4).
  // Without one this loop does not run and the result is bit-identical to the
  // two-pass behaviour that shipped in phase 1.
  let meetsTargetFinish: boolean | null = null;
  if (spec.targetFinish) {
    const target = timeToMinutes(spec.targetFinish);
    const maxHeads = Math.max(heads, Math.floor(spec.maxHeads ?? DEFAULT_MAX_HEADS));
    while (heads < maxHeads && finishFor(heads) > target) heads += 1;
    // Reported, never silently exceeded: if the target needs more people than
    // the operator allows, the honest answer is « not met at 6 », not a seventh
    // cleaner the hotel does not have.
    meetsTargetFinish = finishFor(heads) <= target;
  }

  const start = startFor(heads);
  const finish = Math.min(end, start + Math.ceil(total / heads));

  return {
    activityId: spec.activityId,
    label: spec.label,
    windowStart: minutesToTime(start),
    windowEnd: minutesToTime(finish),
    heads,
    capacityHeads,
    comfortFloor: floor,
    workMinutes: total,
    criticalMinutes: critical,
    meetsTargetFinish,
  };
}

// ── Check-in time — the one genuine step in the window derivation ───────────

/**
 * When check-in opens, which is what the cleaning deadline hangs off.
 *
 * The arrival COUNT is only a proxy and a poor one: a 20-room group arriving at
 * 18:00 is 20 arrivals with no early-check-in pressure whatsoever. The real
 * driver is the distribution of declared arrival times — but `pms_reservation`
 * has no ETA column yet (brief §4.4), so the count rule stands as the documented
 * fallback until it does.
 *
 * `etas` are 'HH:MM' declared arrival times; when enough of them are known the
 * quantile wins. The desk override sits ABOVE this in the UI — group arrivals
 * are exactly what a clerk knows and no formula infers.
 */
export function checkInTime(
  arrivals: number,
  options: {
    etas?: string[];
    /** Fraction of arrivals that must have a declared ETA before we trust them. */
    etaCoverage?: number;
    /** Which quantile of the ETA distribution counts as « the rush ». */
    quantile?: number;
    heavyArrivals?: number;
    early?: string;
    normal?: string;
  } = {},
): string {
  const { etas = [], etaCoverage = 0.6, quantile = 0.25, heavyArrivals = 15, early = '14:00', normal = '15:00' } = options;

  if (arrivals > 0 && etas.length / arrivals >= etaCoverage && etas.length > 0) {
    const sorted = [...etas].sort();
    const index = Math.min(sorted.length - 1, Math.floor(quantile * sorted.length));
    const rush = sorted[index] as string;
    // Never later than the published opening: a late-arriving book does not let
    // the rooms be ready late, it only removes the pressure to be early.
    return rush < normal ? rush : normal;
  }
  return arrivals > heavyArrivals ? early : normal;
}

// ── Archetype C — tiered profile (Dîner) ────────────────────────────────────

export interface TieredPost {
  activityId: number | null;
  label: string;
  windowStart: string;
  windowEnd: string;
  heads: number;
}

export interface TieredBand {
  /** Lower bound (inclusive) of the demand value this band applies to. */
  from: number;
  posts: TieredPost[];
}

export interface TieredSpec {
  kind: 'tiered';
  label: string;
  formula: DemandFormula;
  /** Ordered ascending by `from`; the highest matching band wins. */
  bands: TieredBand[];
}

/**
 * The operator's dinner rule (brief §4.3). Note that the two bands differ in
 * more than head count: below 20 couverts « Plonge + nettoyage » is ONE post
 * held by the service person working on (18:00–24:00 in two legs); above 20 it
 * splits into two posts with different windows. No per-activity field can
 * express a changing decomposition, which is why the band owns a post LIST.
 */
export function resolveTiered(spec: TieredSpec, day: BoardDay | null): ResolvedPost[] {
  const demand = evaluateFormula(day, spec.formula);
  let chosen: TieredBand | null = null;
  for (const band of spec.bands) {
    if (demand >= band.from && (chosen === null || band.from >= chosen.from)) chosen = band;
  }
  if (!chosen) return [];
  return chosen.posts.map((p) => ({ ...p }));
}

// ── Combination — two kinds, and they constrain oppositely ──────────────────

/**
 * Whether one body may hold two posts on the same day.
 *
 * - **Sequential** (windows disjoint): always allowed. ADR-0011 already models
 *   a person holding *Petit-déjeuner* and *Nettoyage*; the low dinner band is
 *   the same shape — service, then plonge.
 * - **Concurrent** (windows overlap): allowed ONLY for a declared pair, e.g.
 *   Bar + Réception. Without this distinction the allocator would happily put
 *   one person on two simultaneous posts they cannot physically hold.
 *
 * The consequence for coverage: total bodies needed ≠ Σ per-post heads.
 */
export function windowsOverlap(a: ResolvedPost, b: ResolvedPost): boolean {
  return timeToMinutes(a.windowStart) < timeToMinutes(b.windowEnd) && timeToMinutes(b.windowStart) < timeToMinutes(a.windowEnd);
}

/** Unordered activity-pair key for the concurrent-combinable set. */
export function combinationKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

export function canHoldBoth(a: ResolvedPost, b: ResolvedPost, concurrentPairs: ReadonlySet<string>): boolean {
  if (!windowsOverlap(a, b)) return true;
  if (a.activityId === null || b.activityId === null) return false;
  return concurrentPairs.has(combinationKey(a.activityId, b.activityId));
}

// ── The one entry point ─────────────────────────────────────────────────────

export type StaffingSpec = BinarySpec | WorkContentSpec | TieredSpec;

/**
 * Resolve every spec for a day into concrete posts. The allocator never sees an
 * archetype — that is the whole point of this module.
 *
 * `workload` and `readyBy` are only consulted by work-content specs; passing
 * them for a day with no Ménage spec is harmless.
 */
export function resolveDayPosts(input: {
  specs: StaffingSpec[];
  day: BoardDay | null;
  workload?: RoomWorkload;
  readyBy?: string;
}): ResolvedPost[] {
  const posts: ResolvedPost[] = [];
  for (const spec of input.specs) {
    if (spec.kind === 'binary') posts.push(...resolveBinary(spec, input.day));
    else if (spec.kind === 'tiered') posts.push(...resolveTiered(spec, input.day));
    else if (input.workload) {
      posts.push(resolveMenage(spec, input.workload, input.readyBy ?? '15:00'));
    }
  }
  return posts;
}

// ── Phase 3 — the derived sliding window ────────────────────────────────────

/**
 * The day's room workload, from the metrics the board already stores.
 *
 * Two inputs the brief calls for do NOT exist as day metrics and are therefore
 * PARAMETERS with honest defaults rather than silent zeros in a formula:
 *
 *   `turnovers`      — needs per-room assignment (`pms_reservation.room_id`).
 *                      Rooms are assigned at booking, so this is exactly
 *                      computable; until the PMS seeding job supplies it, the
 *                      bound `min(àBlancs, arrivées)` is used, which errs by
 *                      starting the shift earlier than needed, never later.
 *   `deepStayovers`  — needs each stay's night index (every 4th night). No
 *                      day-aggregate formula can express it (brief §4.2), so it
 *                      is 0 until the PMS supplies it, and Ménage is
 *                      under-staffed on long-stay weeks. Known, documented, and
 *                      visible rather than hidden inside a coefficient.
 */
export function workloadFromDay(
  day: BoardDay,
  supplied: { turnovers?: number; deepStayovers?: number } = {},
): RoomWorkload {
  return workloadFromCounts(day, supplied);
}

/**
 * Same derivation from the three raw counts, for callers that have live
 * (unsaved) figures rather than a stored `BoardDay` — the week grid's
 * « Charge » row, which must track what the desk is typing.
 *
 * Extracted so that row and the forecast cannot drift apart: one definition of
 * what the day's room work IS.
 */
export function workloadFromCounts(
  counts: { departures: number; arrivals: number; stayovers: number },
  supplied: { turnovers?: number; deepStayovers?: number } = {},
): RoomWorkload {
  const deep = Math.max(0, Math.min(counts.stayovers, supplied.deepStayovers ?? 0));
  return {
    aBlancs: counts.departures,
    turnovers: supplied.turnovers ?? boundTurnovers(counts.departures, counts.arrivals),
    // A deep clean is counted ONCE, at the heavier rate — never in both buckets.
    recouches: Math.max(0, counts.stayovers - deep),
    recouchesAFond: deep,
    arrivees: counts.arrivals,
  };
}

/** A window the model computed for a day, plus the head count behind it. */
export interface DerivedWindow {
  windowStart: string;
  windowEnd: string;
  heads: number;
}

/**
 * Windows the staffing model derives for a given day, by activity id.
 *
 * Only work-content profiles produce one: a binary or tiered post carries its
 * window in configuration, where it is already per-band. This is the whole of
 * « the window slides with demand » (brief §4.4) — a heavy departure day pulls
 * the start earlier by the amount of extra DEADLINE-CRITICAL work, continuously,
 * with no « high departure day » threshold to tune.
 */
export function derivedWindows(input: {
  specs: StaffingSpec[];
  day: BoardDay | null;
  workload?: RoomWorkload;
  /** Check-in opening; compute with `checkInTime` when not overridden by the desk. */
  readyBy?: string;
}): Map<number, DerivedWindow> {
  const out = new Map<number, DerivedWindow>();
  if (!input.day || !input.workload) return out;
  for (const spec of input.specs) {
    if (spec.kind !== 'workContent') continue;
    const resolved = resolveMenage(spec, input.workload, input.readyBy ?? '15:00');
    out.set(spec.activityId, {
      windowStart: resolved.windowStart,
      windowEnd: resolved.windowEnd,
      heads: resolved.heads,
    });
  }
  return out;
}

// ── The board's « prévisionnel » strip (2026-08-01) ─────────────────────────

/**
 * What the desk is shown above the activity Gantt for one day, for one
 * work-content activity: the window the load implies, the load itself in
 * à-blanc equivalents, and the heads that window needs.
 *
 * `aBlancEquivalents` is deliberately the unit here rather than person-minutes:
 * it is the currency the comfort floor is already expressed in, and « 42 à
 * blancs » is a quantity the desk can picture. Heads is `resolveMenage`'s
 * final figure — `max(comfortFloor, capacityHeads)` — not a re-derivation, so
 * the strip can never disagree with what the model actually resolved.
 */
export interface DayForecast {
  activityId: number;
  label: string;
  windowStart: string;
  windowEnd: string;
  heads: number;
  /** The day's total room work, divided by one à blanc. */
  aBlancEquivalents: number;
  /** Why that many heads: the volume-driven figure and the « two is easier » floor. */
  capacityHeads: number;
  comfortFloor: number;
  /** Check-in opening the window was sized against. */
  readyBy: string;
  /** Total person-minutes — the quantity the trade-off divides. */
  workMinutes: number;
  /** What each other head count would imply (ADR-0015 §4). */
  tradeoff: TradeoffOption[];
  /** Whether a configured « terminé avant » target was reached; null if none. */
  meetsTargetFinish: boolean | null;
}

/**
 * The forecast rows for a day. Only work-content profiles appear — a binary or
 * tiered post carries a configured window, so there is nothing to "align to".
 *
 * Returns [] rather than throwing when the day or its workload is missing: an
 * unsaved day legitimately has no forecast, and the strip simply does not render.
 */
export function dayForecasts(input: {
  specs: StaffingSpec[];
  day: BoardDay | null;
  workload?: RoomWorkload;
  readyBy?: string;
}): DayForecast[] {
  if (!input.day || !input.workload) return [];
  const readyBy = input.readyBy ?? '15:00';
  const out: DayForecast[] = [];
  for (const spec of input.specs) {
    if (spec.kind !== 'workContent') continue;
    const resolved = resolveMenage(spec, input.workload, readyBy);
    // Nothing to clean → no forecast row at all, matching comfortFloor's rule
    // that « a handful of rooms » is not zero. A 0-head 08:00–08:00 strip would
    // read as a real shift the desk should staff.
    if (resolved.heads === 0) continue;
    out.push({
      activityId: spec.activityId,
      label: resolved.label,
      windowStart: resolved.windowStart,
      windowEnd: resolved.windowEnd,
      heads: resolved.heads,
      aBlancEquivalents: aBlancEquivalents(input.workload, spec.minutes),
      capacityHeads: resolved.capacityHeads,
      comfortFloor: resolved.comfortFloor,
      readyBy,
      workMinutes: resolved.workMinutes,
      tradeoff: tradeoffFrontier(spec, input.workload, readyBy),
      meetsTargetFinish: resolved.meetsTargetFinish,
    });
  }
  return out;
}

/**
 * What each work-content profile PUBLISHES for a day, keyed by profile id —
 * the bridge between the staffing model and the metric registry (ADR-0015).
 *
 * This is the one place the ½-vs-⅓ contradiction is actually dissolved: the
 * housekeeping load an activity gates on is now *this* number, computed from
 * person-minutes, rather than a second weighted sum restating the same rule
 * with different coefficients.
 *
 * A day with no saved workload yields an EMPTY map, not zeroed outputs — the
 * registry then reports those metrics as null (unknown) rather than 0 (nothing
 * to do), which is the distinction the board's « pas encore saisi » depends on.
 */
export function profileOutputsForDay(input: {
  specs: StaffingSpec[];
  day: BoardDay | null;
  workload?: RoomWorkload;
  readyBy?: string;
}): Map<number, ProfileOutputs> {
  const out = new Map<number, ProfileOutputs>();
  if (!input.day || !input.workload) return out;
  const readyBy = input.readyBy ?? '15:00';
  for (const spec of input.specs) {
    if (spec.kind !== 'workContent') continue;
    const resolved = resolveMenage(spec, input.workload, readyBy);
    out.set(spec.profileId, {
      workMinutes: resolved.workMinutes,
      aBlancEquivalents: aBlancEquivalents(input.workload, spec.minutes),
      heads: resolved.heads,
    });
  }
  return out;
}

/** True when the planned window already matches the forecast — nothing to apply. */
export function forecastMatchesPlanned(
  forecast: { windowStart: string; windowEnd: string },
  planned: { windowStart: string; windowEnd: string },
): boolean {
  return (
    forecast.windowStart === planned.windowStart.slice(0, 5) &&
    forecast.windowEnd === planned.windowEnd.slice(0, 5)
  );
}
